import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { StepIndicator } from './components/StepIndicator';
import { ContactInformation } from './components/steps/ContactInformation';
import { BusinessDetails } from './components/steps/BusinessDetails';
import { LogoExamples } from './components/steps/LogoExamples';
import { BrandPersonality } from './components/steps/BrandPersonality';
import { ColorPreferences } from './components/steps/ColorPreferences';
import { StylePreferences } from './components/steps/StylePreferences';
import { AdditionalNotes } from './components/steps/AdditionalNotes';
import { useLogoForm } from './hooks/useLogoForm';

const buttonBaseStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '14px 24px',
  borderRadius: '9999px',
  fontWeight: '500',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  border: 'none',
  outline: 'none',
};

const primaryButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  backgroundColor: '#6366F1',
  color: '#FFFFFF',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const secondaryButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  backgroundColor: '#FFFFFF',
  color: '#6366F1',
  border: '2px solid #6366F1',
};

const disabledButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  opacity: 0.5,
  cursor: 'not-allowed',
  backgroundColor: '#E5E7EB',
  color: '#9CA3AF',
  border: 'none',
};

function App() {
  const [step, setStep] = useState(1);
  const [attemptedNext, setAttemptedNext] = useState(false);
  const totalSteps = 6;
  
  const {
    formData,
    errors,
    duplicateColorError,
    handleInputChange,
    handleSliderChange,
    handleCheckboxChange,
    handleColorPaletteSelect,
    handleCustomColorChange,
    addColor,
    removeLastColor,
    validateStep,
    handleExport,
    generatePrompt,
    setErrors
  } = useLogoForm();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const handleNextStep = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    setAttemptedNext(true);
    if (validateStep(step)) {
      setStep(prev => Math.min(totalSteps, prev + 1));
      setAttemptedNext(false);
    }
  };

  const handlePrevStep = (e: React.MouseEvent) => {
    e.preventDefault();
    setStep(prev => Math.max(1, prev - 1));
    setAttemptedNext(false);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && 
          e.target instanceof HTMLElement && 
          e.target.tagName.toLowerCase() !== 'textarea' &&
          !e.target.closest('.color-picker')) {
        e.preventDefault();
        handleNextStep();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [step, validateStep]);

  const renderStep = () => {
    const commonProps = {
      formData,
      showErrors: attemptedNext
    };

    switch (step) {
      case 1:
        return (
          <form onSubmit={handleNextStep} noValidate>
            <ContactInformation
              {...commonProps}
              handleInputChange={handleInputChange}
            />
          </form>
        );
      case 2:
        return (
          <form onSubmit={handleNextStep} noValidate>
            <BusinessDetails
              {...commonProps}
              handleInputChange={handleInputChange}
            />
          </form>
        );
      case 3:
        return (
          <LogoExamples
            {...commonProps}
            handleCheckboxChange={handleCheckboxChange}
          />
        );
      case 4:
        return (
          <BrandPersonality
            formData={formData}
            handleSliderChange={handleSliderChange}
          />
        );
      case 5:
        return (
          <ColorPreferences
            {...commonProps}
            errors={errors}
            duplicateColorError={duplicateColorError}
            handleCustomColorChange={handleCustomColorChange}
            handleColorPaletteSelect={handleColorPaletteSelect}
            addColor={addColor}
            removeLastColor={removeLastColor}
            setErrors={setErrors}
          />
        );
      case 6:
        return (
          <AdditionalNotes
            formData={formData}
            handleInputChange={handleInputChange}
            handleExport={handleExport}
            generatedPrompt={generatePrompt}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white pb-32">
      <div className="bg-white">
        <div className="container mx-auto px-4 py-2">
          <img 
            src="https://thefreewebsiteguys.com/wp-content/uploads/2023/01/logo-3.png" 
            alt="Logo" 
            className="h-8 sm:h-10"
          />
        </div>
      </div>

      <div className="container mx-auto px-4 py-4 sm:py-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-blue-900 mb-2">
              Logo Design Brief
            </h1>
            <p className="text-blue-900 text-base sm:text-lg">Create your perfect custom logo with us</p>
          </div>

          <StepIndicator currentStep={step} totalSteps={totalSteps} />

          <div className="step-card p-4 sm:p-8">
            <div className="space-y-8">
              {renderStep()}
            </div>
          </div>
        </div>
      </div>

      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid #F3F4F6',
        boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
        zIndex: 50
      }}>
        <div className="container mx-auto px-4 max-w-4xl">
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0' }}>
            <button
              onClick={handlePrevStep}
              disabled={step === 1}
              style={step === 1 ? disabledButtonStyle : secondaryButtonStyle}
            >
              <ChevronLeft size={20} />
              Back
            </button>
            {step < totalSteps && (
              <button
                onClick={(e) => handleNextStep(e)}
                style={primaryButtonStyle}
              >
                Next
                <ChevronRight size={20} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;